import { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import Layout from '../components/Layout';
import { RootState } from '../store';
import {
  setDocuments,
  addDocument,
  removeDocument,
  setIsUploading,
  Document,
} from '../store/documentsSlice';
import {
  setDocumentAnalysis,
  setAnalyzingDocumentId,
  setAnalysisLoading,
} from '../store/analysisSlice';
import { setProjects } from '../store/projectsSlice';
import { documentApi, documentAnalysisApi, projectsApi } from '../services/api';
import {
  FileUp,
  FileText,
  Trash2,
  ChevronDown,
  ChevronRight,
  Download,
  CheckCircle,
  Clock,
  AlertCircle,
  Loader,
  Sparkles,
  User as UserIcon,
} from 'lucide-react';

export default function Upload() {
  const dispatch = useDispatch();
  const { documents, isUploading } = useSelector((state: RootState) => state.documents);
  const { documentAnalyses, analyzingDocumentId } = useSelector((state: RootState) => state.analysis);
  const { projects, activeProjectId } = useSelector((state: RootState) => state.projects);
  const [expandedDocId, setExpandedDocId] = useState<string | null>(null);
  const [uploadingFiles, setUploadingFiles] = useState<Array<{ id: string; name: string; progress: number; status: string }>>([]);
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});

  // Fetch thumbnail when a document is expanded
  const loadThumbnail = async (docId: string) => {
    if (thumbnails[docId]) return;
    try {
      const res = await documentApi.getThumbnail(parseInt(docId));
      const url = URL.createObjectURL(res.data);
      setThumbnails((prev) => ({ ...prev, [docId]: url }));
    } catch {
      // Thumbnail not available
    }
  };

  // Load documents and projects on mount
  useEffect(() => {
    documentApi.getDocuments(activeProjectId || undefined)
      .then((res) => {
        interface RawBackendDocument {
          id: number | string;
          original_name?: string;
          file_name?: string;
          document_type?: string;
          file_size: number;
          upload_date: string;
          status: string;
          project_id?: number;
          person_id?: number;
          person_name?: string;
          analysis_status?: string;
        }
        const docs = ((res.data.documents || []) as RawBackendDocument[]).map((d) => ({
          id: String(d.id),
          fileName: d.original_name || d.file_name || '',
          documentType: d.document_type || 'OTHER',
          fileSize: d.file_size,
          uploadDate: d.upload_date,
          status: (d.status === 'completed' ? 'uploaded' : d.status) as 'uploaded' | 'processing' | 'error',
          projectId: d.project_id,
          personId: d.person_id,
          personName: d.person_name,
          analysisStatus: d.analysis_status,
        }));
        dispatch(setDocuments(docs));
      })
      .catch(() => {});

    projectsApi.list()
      .then((res) => dispatch(setProjects(res.data)))
      .catch(() => {});
  }, [dispatch, activeProjectId]);

  const activeProject = projects.find((p) => p.id === activeProjectId);

  // File upload handler
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    dispatch(setIsUploading(true));

    for (const file of acceptedFiles) {
      const tempId = `uploading-${Date.now()}-${file.name}`;
      setUploadingFiles((prev) => [...prev, { id: tempId, name: file.name, progress: 50, status: 'uploading' }]);

      try {
        const response = await documentApi.upload(file);
        const data = response.data;

        const doc: Document = {
          id: String(data.id),
          fileName: data.original_name || data.file_name,
          documentType: data.document_type || 'OTHER',
          fileSize: data.file_size,
          uploadDate: data.upload_date,
          status: 'uploaded',
          projectId: data.project_id,
          personId: data.person_id,
          personName: data.person_name,
          analysisStatus: data.analysis_status || 'pending',
        };

        dispatch(addDocument(doc));
        setUploadingFiles((prev) => prev.filter((f) => f.id !== tempId));
      } catch {
        setUploadingFiles((prev) =>
          prev.map((f) => (f.id === tempId ? { ...f, status: 'error', progress: 0 } : f))
        );
      }
    }

    dispatch(setIsUploading(false));
  }, [dispatch]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    maxSize: 200 * 1024 * 1024,
  });

  // Analyze a single document
  const handleAnalyze = async (docId: string) => {
    const numId = parseInt(docId, 10);
    dispatch(setAnalyzingDocumentId(numId));
    dispatch(setAnalysisLoading(true));
    try {
      const res = await documentAnalysisApi.analyzeDocument(numId);
      if (res.data?.analysis) {
        dispatch(setDocumentAnalysis({
          documentId: numId,
          analysis: {
            documentId: numId,
            documentType: res.data.analysis.document_type || 'OTHER',
            confidence: res.data.analysis.confidence || 0,
            extractedFields: Object.entries(res.data.analysis.fields || {}).map(([k, v]) => ({
              fieldName: k,
              value: v as string | number,
            })),
            issuerPayerInfo: res.data.analysis.issuer,
            observations: res.data.analysis.observations || [],
            warnings: (res.data.analysis.warnings || []).map((w: string) => ({ severity: 'warning' as const, message: w })),
            rawData: res.data.analysis,
          },
        }));
        setExpandedDocId(docId);
      }
    } catch {
      // Error handled silently
    } finally {
      dispatch(setAnalyzingDocumentId(null));
      dispatch(setAnalysisLoading(false));
    }
  };

  // Delete document
  const handleDelete = async (docId: string) => {
    try {
      await documentApi.deleteDocument(docId);
      dispatch(removeDocument(docId));
    } catch {
      // Error handled silently
    }
  };

  // Assign person to document
  const handleAssignPerson = async (docId: string, personId: number) => {
    try {
      await documentApi.assignDocument(parseInt(docId), activeProjectId || 0, personId);
      // Update local state
      const person = activeProject?.persons.find((p) => p.id === personId);
      dispatch(setDocuments(
        documents.map((d) => d.id === docId ? { ...d, personId, personName: person?.name } : d)
      ));
    } catch {
      // Error handled silently
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatCurrency = (val: unknown) => {
    const num = parseFloat(String(val));
    if (isNaN(num)) return String(val);
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getStatusIcon = (doc: Document) => {
    const analysis = documentAnalyses[parseInt(doc.id)];
    if (analysis) return <CheckCircle className="w-3.5 h-3.5 text-green-500" />;
    if (doc.analysisStatus === 'complete') return <CheckCircle className="w-3.5 h-3.5 text-green-500" />;
    if (analyzingDocumentId === parseInt(doc.id)) return <Loader className="w-3.5 h-3.5 text-blue-500 animate-spin" />;
    return <Clock className="w-3.5 h-3.5 text-gray-400" />;
  };

  const getStatusText = (doc: Document) => {
    const analysis = documentAnalyses[parseInt(doc.id)];
    if (analysis) return 'Analyzed';
    if (doc.analysisStatus === 'complete') return 'Analyzed';
    if (analyzingDocumentId === parseInt(doc.id)) return 'Analyzing...';
    return 'Uploaded';
  };

  return (
    <Layout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Documents</h1>
        <p className="text-sm text-gray-500">
          {activeProject ? `${activeProject.name} — ${activeProject.taxYear}` : 'Upload your tax documents'}
        </p>
      </div>

      {/* Drop Zone */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all mb-8 ${
          isDragActive
            ? 'border-gray-900 bg-gray-50'
            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
        }`}
      >
        <input {...getInputProps()} />
        <FileUp className={`w-8 h-8 mx-auto mb-3 ${isDragActive ? 'text-gray-900' : 'text-gray-400'}`} />
        <p className="text-sm font-medium text-gray-700">
          {isDragActive ? 'Drop files here' : 'Drag & drop tax documents, or click to browse'}
        </p>
        <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG — up to 200MB</p>
      </div>

      <div className="mb-8 rounded-xl border border-gray-200 bg-gray-50 p-5">
        <div className="flex items-start gap-3">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-500" />
          <div>
            <h2 className="text-sm font-semibold text-gray-900">How Filie protects uploaded documents</h2>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-6 text-gray-600">
              <li>SSNs and EINs are redacted from document images before AI processing in Filie&apos;s current production pipeline.</li>
              <li>Uploads are checked for valid file content, not just file extensions.</li>
              <li>Stored document objects use encrypted storage controls and server-generated object keys.</li>
            </ul>
            <a href="/security" className="mt-3 inline-block text-sm font-medium text-gray-900 underline underline-offset-2">
              Read Filie&apos;s security overview
            </a>
          </div>
        </div>
      </div>

      {/* Uploading Files */}
      {uploadingFiles.length > 0 && (
        <div className="mb-6 space-y-2">
          {uploadingFiles.map((f) => (
            <div key={f.id} className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg text-sm">
              <Loader className="w-4 h-4 text-blue-500 animate-spin" />
              <span className="text-gray-700">{f.name}</span>
              {f.status === 'error' && <span className="text-red-500 text-xs">Upload failed</span>}
            </div>
          ))}
        </div>
      )}

      {/* Document List */}
      {documents.length === 0 && !isUploading ? (
        <div className="text-center py-16">
          <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">No documents uploaded yet</p>
        </div>
      ) : (
        <div className="rounded-xl border border-gray-100">
          {documents.map((doc) => {
            const isExpanded = expandedDocId === doc.id;
            const analysis = documentAnalyses[parseInt(doc.id)];
            const isAnalyzing = analyzingDocumentId === parseInt(doc.id);

            return (
              <div key={doc.id} className="border-b border-gray-100 last:border-0">
                {/* Document Row */}
                <div
                  className="flex items-center px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => {
                    const newId = isExpanded ? null : doc.id;
                    setExpandedDocId(newId);
                    if (newId) loadThumbnail(newId);
                  }}
                >
                  {/* Expand icon */}
                  <div className="w-6 flex-shrink-0">
                    {isExpanded
                      ? <ChevronDown className="w-4 h-4 text-gray-400" />
                      : <ChevronRight className="w-4 h-4 text-gray-400" />
                    }
                  </div>

                  {/* Doc type badge */}
                  <div className="w-16 flex-shrink-0">
                    <span className="text-[10px] font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded uppercase">
                      {doc.documentType.replace('_', '-').replace('FORM_', '')}
                    </span>
                  </div>

                  {/* File name */}
                  <div className="flex-1 min-w-0 px-3">
                    <p className="text-sm font-medium text-gray-900 truncate">{doc.fileName}</p>
                    <p className="text-xs text-gray-400">{formatFileSize(doc.fileSize)}</p>
                  </div>

                  {/* Person */}
                  <div className="w-32 flex-shrink-0 hidden md:block">
                    {activeProject && activeProject.persons.length > 1 ? (
                      <select
                        value={doc.personId || ''}
                        onChange={(e) => {
                          e.stopPropagation();
                          if (e.target.value) handleAssignPerson(doc.id, parseInt(e.target.value));
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs border border-gray-200 rounded px-2 py-1 w-full bg-white"
                      >
                        <option value="">Unassigned</option>
                        {activeProject.persons.map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-xs text-gray-400 flex items-center">
                        <UserIcon className="w-3 h-3 mr-1" />
                        {doc.personName || 'You'}
                      </span>
                    )}
                  </div>

                  {/* Status */}
                  <div className="w-24 flex-shrink-0 flex items-center space-x-1.5">
                    {getStatusIcon(doc)}
                    <span className="text-xs text-gray-500">{getStatusText(doc)}</span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    {!analysis && doc.analysisStatus !== 'complete' && (
                      <button
                        onClick={() => handleAnalyze(doc.id)}
                        disabled={isAnalyzing}
                        className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors disabled:opacity-50"
                        title="Analyze document"
                      >
                        {isAnalyzing
                          ? <Loader className="w-4 h-4 animate-spin" />
                          : <Sparkles className="w-4 h-4" />
                        }
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(doc.id)}
                      className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                      title="Delete document"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Expanded Detail Panel */}
                {isExpanded && (
                  <div className="px-10 pb-4 bg-gray-50 border-t border-gray-100">
                    {analysis ? (
                      <div className="py-4 space-y-4">
                        <div className="flex gap-5">
                        {/* Thumbnail */}
                        {thumbnails[doc.id] && (
                          <div className="flex-shrink-0">
                            <img src={thumbnails[doc.id]} alt="Page 1" className="w-24 rounded border border-gray-200 shadow-sm" />
                          </div>
                        )}
                        <div className="flex-1 space-y-4">
                        {/* Header */}
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">
                              {analysis.documentType} {analysis.issuerPayerInfo?.name ? `— ${analysis.issuerPayerInfo.name}` : ''}
                            </p>
                            {analysis.confidence > 0 && (
                              <p className="text-xs text-gray-400">Confidence: {Math.round(analysis.confidence * 100)}%</p>
                            )}
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleAnalyze(doc.id)}
                              className="text-xs text-gray-500 hover:text-gray-800 border border-gray-200 px-2.5 py-1 rounded"
                            >
                              Re-analyze
                            </button>
                            <a
                              href={`/api/documents/${doc.id}/download`}
                              className="text-xs text-gray-500 hover:text-gray-800 border border-gray-200 px-2.5 py-1 rounded flex items-center"
                            >
                              <Download className="w-3 h-3 mr-1" /> Download
                            </a>
                          </div>
                        </div>

                        {/* Extracted Values */}
                        {analysis.extractedFields && analysis.extractedFields.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Extracted Values</p>
                            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                              {analysis.extractedFields.map((field, idx) => (
                                <div key={idx} className="flex justify-between text-sm">
                                  <span className="text-gray-600">{field.fieldName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                                  <span className="font-medium text-gray-900">
                                    {typeof field.value === 'number' ? formatCurrency(field.value) : String(field.value)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Observations */}
                        {analysis.observations && analysis.observations.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Observations</p>
                            <ul className="space-y-0.5">
                              {analysis.observations.map((obs, idx) => (
                                <li key={idx} className="text-xs text-gray-600 flex items-start">
                                  <span className="text-gray-400 mr-1.5">•</span>{obs}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Warnings */}
                        {analysis.warnings && analysis.warnings.length > 0 && (
                          <div>
                            {analysis.warnings.map((w, idx) => (
                              <div key={idx} className="flex items-start space-x-1.5 text-xs text-amber-700 bg-amber-50 rounded p-2">
                                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                <span>{w.message}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      </div>
                      </div>
                    ) : (
                      <div className="py-6 text-center">
                        <p className="text-sm text-gray-500 mb-3">This document hasn't been analyzed yet.</p>
                        <button
                          onClick={() => handleAnalyze(doc.id)}
                          disabled={isAnalyzing}
                          className="inline-flex items-center space-x-1.5 px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-full text-sm font-medium transition-colors disabled:opacity-50"
                        >
                          <Sparkles className="w-3.5 h-3.5" />
                          <span>{isAnalyzing ? 'Analyzing...' : 'Analyze Document'}</span>
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Layout>
  );
}
